/**
 * Distractor fetching for generated MCQ / reverse-MCQ / fill activities.
 *
 * Reuses the existing feed distractor endpoint (`postGenerateDistractors` →
 * `/auth/activity/distractors`) rather than duplicating it, and adds the iOS
 * 3-second timeout race (`activityCoordinator.ts:33,801-803`): if distractors
 * don't return in time we resolve to `[]` and the caller falls back to random
 * words. Never throws — failure → empty array.
 */
import { callWithAuth } from "@/api/Api";
import { postGenerateDistractors } from "@/feed/api/feedApi";

/** Timeout for distractor API calls (ms) — iOS DISTRACTOR_TIMEOUT_MS. */
export const DISTRACTOR_TIMEOUT_MS = 3000;

/** Race a promise against a timeout, resolving to `fallback` if it fires first. */
export function withTimeout<T>(
  promise: Promise<T>,
  fallback: T,
  ms: number,
): Promise<T> {
  // If the timeout wins the race, `promise` may still reject later; attach a
  // no-op catch so that late rejection doesn't surface as an unhandled promise
  // rejection after generation already continued with the fallback.
  promise.catch(() => {});
  return Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

export interface DistractorRequest {
  wordKey: string;
  correctNativeWord: string;
  availableWords: string[];
  videoLevel: number;
  distractorLanguage: "english" | "spanish";
}

/**
 * Fetch distractors via the cached LLM endpoint, with a 3s timeout race.
 * Returns `[]` on timeout or any error — callers top-up from random words.
 */
export async function fetchDistractors(
  req: DistractorRequest,
): Promise<string[]> {
  try {
    const res = await withTimeout(
      callWithAuth(postGenerateDistractors, req),
      { distractors: [] as string[] },
      DISTRACTOR_TIMEOUT_MS,
    );
    return res?.distractors ?? [];
  } catch {
    return [];
  }
}
